import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { firstValueFrom } from 'rxjs';
import { CustomsClearanceService } from '../../services/customs-clearance.service';
import { DocumentService } from '../../services/document.service';
import { 
  CustomsClearanceRequest, 
  CustomsClearanceEvent, 
  PaginatedResponse,
  CustomsFilters
} from '../../../models/document.models';

// Interface for eligible cartons (reuse from existing services)
interface CartonResponse {
  carrierTrackingNumber: string;
  orderId: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  repackedDate: string;
  status: string;
  destinationRegion?: string;
  destinationRegionName?: string;
}

// Interface for bulk update result
interface BulkUpdateResult {
  totalProcessed: number;
  successful: CartonResponse[];
  failed: Array<{ carton: CartonResponse; error: string }>;
  isComplete: boolean;
}

// Interface for bulk update progress
interface BulkUpdateProgress {
  current: number;
  total: number;
  percentage: number;
  currentCarton?: string;
}

@Component({
  selector: 'app-customs-clearance',
  standalone: true,
  imports: [CommonModule, FormsModule, ...HlmCardImports],
  templateUrl: './customs-clearance.component.html',
  styleUrls: ['./customs-clearance.component.css']
})
export class CustomsClearanceComponent implements OnInit {
  isLoading = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;

  // Tab management - Updated to include multiple tab
  activeTab: 'clearance' | 'multiple' | 'pending' | 'history' = 'clearance';

  // Eligible cartons for customs clearance
  eligibleCartons: CartonResponse[] = [];
  eligibleCartonsLoading = false;
  selectedCarton: CartonResponse | null = null;
  showEligibleCartons = true;
  cartonFilters = {
    pageSize: 10,
    pageNumber: 1
  };
  totalCartons = 0;
  totalCartonPages = 0;

  // Multiple carton selection for batch clearance
  selectedCartons: CartonResponse[] = [];
  showSelectedCartons = false;
  isMultipleClearanceUpdating = false;
  multipleClearanceUpdateProgress: BulkUpdateProgress | null = null;
  multipleClearanceUpdateResult: BulkUpdateResult | null = null;
  showMultipleClearanceResult = false;

  // Multiple clearance tab specific properties
  multipleCartons: CartonResponse[] = [];
  selectedMultipleCartons: Set<string> = new Set();
  isLoadingMultipleCartons = false;
  multipleCartonFilters = {
    cartonNumber: '',
    orderId: '',
    status: '',
    pageSize: 10,
    pageNumber: 1
  };
  multipleCartonPagination: any = null;
  isMultipleBatchProcessing = false;
  batchProgress = {
    processed: 0,
    total: 0,
    errors: [] as string[]
  };
  batchResult: any = null;
  batchClearanceForm = {
    customsStatus: '',
    clearedAtUtc: '',
    remarks: '',
    destinationRegion: '',
    customsRegion: '',
    customsDirection: '',
    brokerName: '',
    portCode: '',
    clearanceWay: ''
  };

  // Pending customs clearances
  pendingClearances: CustomsClearanceEvent[] = [];
  pendingClearancesLoading = false;
  pendingFilters: CustomsFilters = {
    pageSize: 10,
    pageNumber: 1
  };
  totalPendingClearances = 0;
  totalPendingPages = 0;

  // Customs clearance history
  clearanceHistory: CustomsClearanceEvent[] = [];
  selectedCarrierTN = '';
  historyLoading = false;

  // Customs clearance form data
  customsClearance: CustomsClearanceRequest = {
    carrier_tn: '',
    destination_region: '',
    customs_region: '',
    customs_dir: '',
    cc_broker_name: '',
    cc_port_code: '',
    cc_way: ''
  };

  // Customs direction options
  customsDirections = [
    { value: 'import', label: 'Import' },
    { value: 'export', label: 'Export' },
    { value: 'transit', label: 'Transit' }
  ];

  // Common regions (reuse from existing components)
  regions = [
    { value: 'MY', label: 'Malaysia' },
    { value: 'SG', label: 'Singapore' },
    { value: 'TH', label: 'Thailand' },
    { value: 'VN', label: 'Vietnam' },
    { value: 'PH', label: 'Philippines' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'TW', label: 'Taiwan' },
    { value: 'BR', label: 'Brazil' },
    { value: 'MX', label: 'Mexico' },
    { value: 'CL', label: 'Chile' },
    { value: 'CO', label: 'Colombia' },
    { value: 'PL', label: 'Poland' },
    { value: 'ES', label: 'Spain' },
    { value: 'FR', label: 'France' },
    { value: 'CN', label: 'China' },
    { value: 'HK', label: 'Hong Kong' },
    { value: 'JP', label: 'Japan' },
    { value: 'KR', label: 'South Korea' },
    { value: 'AU', label: 'Australia' },
    { value: 'NZ', label: 'New Zealand' }
  ];

  // Clearance ways
  clearanceWays = [
    { value: 'normal', label: 'Normal Clearance' },
    { value: 'express', label: 'Express Clearance' },
    { value: 'green_channel', label: 'Green Channel' },
    { value: 'red_channel', label: 'Red Channel' },
    { value: 'yellow_channel', label: 'Yellow Channel' },
    { value: 'blue_channel', label: 'Blue Channel' }
  ];

  constructor(
    private customsClearanceService: CustomsClearanceService,
    private documentService: DocumentService
  ) {}

  ngOnInit(): void {
    this.loadTabData();
  }

  // Clear success/error messages
  clearMessages(): void {
    this.successMessage = null;
    this.errorMessage = null;
  }

  // Tab management methods
  switchTab(tab: 'clearance' | 'multiple' | 'pending' | 'history'): void {
    this.activeTab = tab;
    this.clearMessages();
    this.loadTabData();
  }

  // Load data based on active tab
  loadTabData(): void {
    switch (this.activeTab) {
      case 'clearance':
        this.loadEligibleCartons();
        break;
      case 'multiple':
        this.loadMultipleCartons();
        break;
      case 'pending':
        this.loadPendingClearances();
        break;
      case 'history':
        // History is loaded on demand when carrier TN is entered
        break;
    }
  }

  // Load eligible cartons for customs clearance (using same method as shipping progress)
  loadEligibleCartons(): void {
    this.eligibleCartonsLoading = true;

    // Use DocumentService with isForCustomsClearanceInfo flag set to true
    this.documentService.getEligibleCartons(this.cartonFilters, false, true).subscribe({
      next: (response) => {
        console.log('Eligible cartons response:', response);
        
        // Handle the response structure - it might be wrapped or direct
        if (response && response.items) {
          this.eligibleCartons = response.items || [];
          this.totalCartons = response.totalCount || 0;
          this.totalCartonPages = response.totalPages || 0;
        } else if (response && response.success && response.data) {
          // Check both Items (PascalCase) and items (camelCase)
          this.eligibleCartons = response.data.Items || response.data.items || [];
          this.totalCartons = response.data.TotalCount || response.data.totalCount || 0;
          this.totalCartonPages = response.data.TotalPages || response.data.totalPages || 0;
        } else if (Array.isArray(response)) {
          this.eligibleCartons = response;
          this.totalCartons = response.length;
          this.totalCartonPages = 1;
        } else {
          console.warn('Unexpected response format:', response);
          this.eligibleCartons = [];
        }
        
        if (this.eligibleCartons.length === 0) {
          this.successMessage = 'No cartons available for customs clearance. Cartons must be in appropriate status.';
        } else {
          this.successMessage = `Found ${this.eligibleCartons.length} carton(s) eligible for customs clearance`;
        }
        
        this.eligibleCartonsLoading = false;
      },
      error: (error) => {
        console.error('Error loading eligible cartons:', error);
        this.errorMessage = error.error?.message || error.message || 'Failed to load eligible cartons';
        this.eligibleCartons = [];
        this.eligibleCartonsLoading = false;
      }
    });
  }

  // Load pending customs clearances
  loadPendingClearances(): void {
    this.pendingClearancesLoading = true;
    this.customsClearanceService.getPendingCustomsClearances(this.pendingFilters).subscribe({
      next: (response) => {
        console.log('Pending clearances response:', response);
        this.pendingClearances = response.items || [];
        this.totalPendingClearances = response.totalCount || 0;
        this.totalPendingPages = Math.ceil(this.totalPendingClearances / this.pendingFilters.pageSize);
        this.pendingClearancesLoading = false;
      },
      error: (error) => {
        console.error('Error loading pending clearances:', error);
        this.errorMessage = 'Failed to load pending clearances';
        this.pendingClearancesLoading = false;
      }
    });
  }

  // Load customs clearance history for a specific carrier tracking number
  loadClearanceHistory(): void {
    if (!this.selectedCarrierTN.trim()) {
      this.errorMessage = 'Please enter a carrier tracking number';
      return;
    }

    this.historyLoading = true;
    this.clearanceHistory = [];
    
    this.customsClearanceService.getCustomsClearanceStatus(this.selectedCarrierTN).subscribe({
      next: (response) => {
        console.log('Clearance history response:', response);
        if (response.success && response.data) {
          this.clearanceHistory = response.data;
          if (this.clearanceHistory.length === 0) {
            this.errorMessage = `No customs clearance history found for ${this.selectedCarrierTN}`;
          }
        } else {
          this.errorMessage = response.message || 'Failed to load clearance history';
        }
        this.historyLoading = false;
      },
      error: (error) => {
        console.error('Error loading clearance history:', error);
        this.errorMessage = 'Failed to load clearance history';
        this.historyLoading = false;
      }
    });
  }

  // Submit customs clearance request
  submitCustomsClearance(): void {
    if (!this.isCustomsClearanceValid()) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isLoading = true;
    this.clearMessages();

    this.customsClearanceService.processCustomsClearance(this.customsClearance).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'Customs clearance request submitted successfully';
          this.resetCustomsClearanceForm();
          this.loadEligibleCartons(); // Refresh the cartons list
        } else {
          this.errorMessage = response.message || 'Failed to submit customs clearance request';
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error submitting customs clearance:', error);
        this.errorMessage = error.error?.message || error.message || 'Failed to submit customs clearance request';
        this.isLoading = false;
      }
    });
  }

  // Validate customs clearance form
  isCustomsClearanceValid(): boolean {
    return !!(
      this.customsClearance.carrier_tn &&
      this.customsClearance.destination_region &&
      this.customsClearance.customs_region &&
      this.customsClearance.customs_dir &&
      this.customsClearance.cc_broker_name &&
      this.customsClearance.cc_port_code &&
      this.customsClearance.cc_way
    );
  }

  // Reset customs clearance form
  resetCustomsClearanceForm(): void {
    this.customsClearance = {
      carrier_tn: '',
      destination_region: '',
      customs_region: '',
      customs_dir: '',
      cc_broker_name: '',
      cc_port_code: '',
      cc_way: ''
    };
    this.selectedCarton = null;
    this.clearMessages();
  }

  // Toggle eligible cartons section
  toggleEligibleCartons(): void {
    this.showEligibleCartons = !this.showEligibleCartons;
  }

  // Select carton for customs clearance
  selectCartonForClearance(carton: CartonResponse): void {
    this.selectedCarton = carton;
    this.customsClearance.carrier_tn = carton.carrierTrackingNumber;
    this.successMessage = `Selected carton ${carton.carrierTrackingNumber} for customs clearance`;
  }

  // Clear selected carton
  clearSelectedCarton(): void {
    this.selectedCarton = null;
    this.customsClearance.carrier_tn = '';
    this.successMessage = null;
  }

  // Multiple carton selection methods (adapted from shipping progress)
  isCartonSelected(carton: CartonResponse): boolean {
    return this.selectedCartons.some(c => c.carrierTrackingNumber === carton.carrierTrackingNumber);
  }

  toggleCartonSelection(carton: CartonResponse, event: any): void {
    if (event.target.checked) {
      if (!this.isCartonSelected(carton)) {
        this.selectedCartons.push(carton);
      }
    } else {
      this.selectedCartons = this.selectedCartons.filter(
        c => c.carrierTrackingNumber !== carton.carrierTrackingNumber
      );
    }
  }

  removeFromSelectedCartons(carton: CartonResponse): void {
    this.selectedCartons = this.selectedCartons.filter(
      c => c.carrierTrackingNumber !== carton.carrierTrackingNumber
    );
  }

  clearSelectedCartons(): void {
    this.selectedCartons = [];
    this.showSelectedCartons = false;
  }

  selectAllVisibleCartons(): void {
    this.eligibleCartons.forEach(carton => {
      if (!this.isCartonSelected(carton)) {
        this.selectedCartons.push(carton);
      }
    });
  }

  areAllVisibleCartonsSelected(): boolean {
    return this.eligibleCartons.length > 0 && 
           this.eligibleCartons.every(carton => this.isCartonSelected(carton));
  }

  areSomeCartonsSelected(): boolean {
    return this.selectedCartons.length > 0 && !this.areAllVisibleCartonsSelected();
  }

  toggleAllVisibleCartons(event: any): void {
    if (event.target.checked) {
      this.selectAllVisibleCartons();
    } else {
      // Remove only visible cartons from selection
      this.eligibleCartons.forEach(carton => {
        this.removeFromSelectedCartons(carton);
      });
    }
  }

  toggleSelectedCartonsView(): void {
    this.showSelectedCartons = !this.showSelectedCartons;
  }

  // Submit multiple customs clearance requests
  async submitMultipleCustomsClearance(): Promise<void> {
    if (this.selectedCartons.length === 0) {
      this.errorMessage = 'Please select at least one carton for multiple customs clearance';
      return;
    }

    if (!this.isCustomsClearanceValid()) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isMultipleClearanceUpdating = true;
    this.clearMessages();
    
    // Initialize progress tracking
    this.multipleClearanceUpdateProgress = {
      current: 0,
      total: this.selectedCartons.length,
      percentage: 0,
      currentCarton: ''
    };

    const successful: CartonResponse[] = [];
    const failed: Array<{ carton: CartonResponse; error: string }> = [];

    // Process cartons sequentially
    for (let i = 0; i < this.selectedCartons.length; i++) {
      const carton = this.selectedCartons[i];
      
      // Update progress
      this.multipleClearanceUpdateProgress.current = i + 1;
      this.multipleClearanceUpdateProgress.percentage = Math.round(((i + 1) / this.selectedCartons.length) * 100);
      this.multipleClearanceUpdateProgress.currentCarton = carton.carrierTrackingNumber;

      try {
        // Create request for each carton
        const request: CustomsClearanceRequest = {
          ...this.customsClearance,
          carrier_tn: carton.carrierTrackingNumber,
          destination_region: carton.destinationRegion || this.customsClearance.destination_region
        };

        // Submit individual clearance request
        const response = await this.customsClearanceService.processCustomsClearance(request).toPromise();
        
        if (response?.success) {
          successful.push(carton);
        } else {
          failed.push({ carton, error: response?.message || 'Unknown error' });
        }
      } catch (error: any) {
        failed.push({ carton, error: error.error?.message || error.message || 'Network error' });
      }

      // Add small delay between requests to avoid overwhelming the server
      if (i < this.selectedCartons.length - 1) {
        await this.delay(100);
      }
    }

    // Update final results
    this.multipleClearanceUpdateResult = {
      totalProcessed: this.selectedCartons.length,
      successful,
      failed,
      isComplete: true
    };

    this.isMultipleClearanceUpdating = false;
    this.multipleClearanceUpdateProgress = null;
    this.showMultipleClearanceResult = true;

    // Show success/error message
    if (failed.length === 0) {
      this.successMessage = `Successfully submitted customs clearance for all ${successful.length} cartons`;
    } else if (successful.length === 0) {
      this.errorMessage = `Failed to submit customs clearance for all ${failed.length} cartons`;
    } else {
      this.successMessage = `Submitted customs clearance for ${successful.length} cartons. ${failed.length} failed.`;
    }

    // Refresh the eligible cartons list
    this.loadEligibleCartons();
    
    // Clear selected cartons
    this.selectedCartons = [];
  }

  // Retry failed cartons from multiple clearance update
  async retryFailedClearances(): Promise<void> {
    if (!this.multipleClearanceUpdateResult || this.multipleClearanceUpdateResult.failed.length === 0) {
      return;
    }

    const failedCartons = this.multipleClearanceUpdateResult.failed.map(f => f.carton);
    this.selectedCartons = failedCartons;
    this.multipleClearanceUpdateResult = null;
    this.showMultipleClearanceResult = false;
    
    // Start the batch update again with failed cartons
    await this.submitMultipleCustomsClearance();
  }

  // Helper method to add delay
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Tab check methods
  isClearanceTabActive(): boolean {
    return this.activeTab === 'clearance';
  }

  isMultipleTabActive(): boolean {
    return this.activeTab === 'multiple';
  }

  isPendingTabActive(): boolean {
    return this.activeTab === 'pending';
  }

  isHistoryTabActive(): boolean {
    return this.activeTab === 'history';
  }

  // Multiple clearance methods
  loadMultipleCartons(): void {
    this.isLoadingMultipleCartons = true;
    
    // Use same service method as eligible cartons with the same logic
    this.documentService.getEligibleCartons(this.multipleCartonFilters, false, true).subscribe({
      next: (response) => {
        console.log('Multiple cartons response:', response);
        
        // Handle the response structure - same as loadEligibleCartons
        if (response && response.items) {
          this.multipleCartons = response.items || [];
          this.multipleCartonPagination = {
            totalPages: response.totalPages || 0,
            totalItems: response.totalCount || 0
          };
        } else if (response && response.success && response.data) {
          // Check both Items (PascalCase) and items (camelCase)
          this.multipleCartons = response.data.Items || response.data.items || [];
          this.multipleCartonPagination = {
            totalPages: response.data.TotalPages || response.data.totalPages || 0,
            totalItems: response.data.TotalCount || response.data.totalCount || 0
          };
        } else if (Array.isArray(response)) {
          this.multipleCartons = response;
          this.multipleCartonPagination = {
            totalPages: 1,
            totalItems: response.length
          };
        } else {
          console.warn('Unexpected response format:', response);
          this.multipleCartons = [];
          this.multipleCartonPagination = {
            totalPages: 0,
            totalItems: 0
          };
        }
        
        this.isLoadingMultipleCartons = false;
      },
      error: (error) => {
        console.error('Error loading multiple cartons:', error);
        this.errorMessage = error.error?.message || error.message || 'Failed to load cartons for multiple clearance';
        this.multipleCartons = [];
        this.isLoadingMultipleCartons = false;
      }
    });
  }

  clearMultipleFilters(): void {
    this.multipleCartonFilters = {
      cartonNumber: '',
      orderId: '',
      status: '',
      pageSize: 10,
      pageNumber: 1
    };
    this.loadMultipleCartons();
  }

  isAllMultipleCartonsSelected(): boolean {
    return this.multipleCartons.length > 0 && 
           this.multipleCartons.every(carton => this.selectedMultipleCartons.has(carton.carrierTrackingNumber));
  }

  isSomeMultipleCartonsSelected(): boolean {
    return this.selectedMultipleCartons.size > 0 && !this.isAllMultipleCartonsSelected();
  }

  toggleAllMultipleCartons(): void {
    if (this.isAllMultipleCartonsSelected()) {
      // Unselect all
      this.multipleCartons.forEach(carton => {
        this.selectedMultipleCartons.delete(carton.carrierTrackingNumber);
      });
    } else {
      // Select all
      this.multipleCartons.forEach(carton => {
        this.selectedMultipleCartons.add(carton.carrierTrackingNumber);
      });
    }
  }

  toggleMultipleCartonSelection(cartonTrackingNumber: string): void {
    if (this.selectedMultipleCartons.has(cartonTrackingNumber)) {
      this.selectedMultipleCartons.delete(cartonTrackingNumber);
    } else {
      this.selectedMultipleCartons.add(cartonTrackingNumber);
    }
  }

  clearMultipleSelection(): void {
    this.selectedMultipleCartons.clear();
  }

  openMultipleClearanceModal(): void {
    // Reset batch form
    this.batchClearanceForm = {
      customsStatus: '',
      clearedAtUtc: '',
      remarks: '',
      destinationRegion: '',
      customsRegion: '',
      customsDirection: '',
      brokerName: '',
      portCode: '',
      clearanceWay: ''
    };
    this.batchResult = null;
    this.isMultipleBatchProcessing = false;
    
    // Open modal (using Bootstrap modal if available)
    const modal = document.getElementById('multipleClearanceModal');
    if (modal) {
      const bsModal = new (window as any).bootstrap.Modal(modal);
      bsModal.show();
    }
  }

  getSelectedMultipleCartonsArray(): CartonResponse[] {
    return this.multipleCartons.filter(carton => 
      this.selectedMultipleCartons.has(carton.carrierTrackingNumber)
    );
  }

  submitBatchClearance(): void {
    if (!this.isBatchFormValid()) {
      this.errorMessage = 'Please fill in all required fields';
      return;
    }

    this.isMultipleBatchProcessing = true;
    this.batchProgress = {
      processed: 0,
      total: this.selectedMultipleCartons.size,
      errors: []
    };

    const selectedCartons = this.getSelectedMultipleCartonsArray();
    let successCount = 0;
    let errorCount = 0;
    const errors: Array<{cartonNumber: string, error: string}> = [];

    // Process cartons sequentially
    this.processBatchClearanceSequentially(selectedCartons, 0, successCount, errorCount, errors);
  }

  private async processBatchClearanceSequentially(
    cartons: CartonResponse[], 
    index: number, 
    successCount: number, 
    errorCount: number, 
    errors: Array<{cartonNumber: string, error: string}>
  ): Promise<void> {
    if (index >= cartons.length) {
      // All cartons processed
      this.batchResult = {
        successCount,
        errorCount,
        errors
      };
      this.isMultipleBatchProcessing = false;
      
      if (errorCount === 0) {
        this.successMessage = `Successfully processed clearance for ${successCount} cartons`;
      } else {
        this.errorMessage = `Processed ${successCount} cartons successfully, ${errorCount} failed`;
      }
      
      // Refresh cartons list
      this.loadMultipleCartons();
      this.selectedMultipleCartons.clear();
      return;
    }

    const carton = cartons[index];
    this.batchProgress.processed = index + 1;

    // Create clearance request for this carton
    const clearanceRequest: CustomsClearanceRequest = {
      carrier_tn: carton.carrierTrackingNumber,
      destination_region: this.batchClearanceForm.destinationRegion,
      customs_region: this.batchClearanceForm.customsRegion,
      customs_dir: this.batchClearanceForm.customsDirection,
      cc_broker_name: this.batchClearanceForm.brokerName,
      cc_port_code: this.batchClearanceForm.portCode,
      cc_way: this.batchClearanceForm.clearanceWay
    };

    try {
      await firstValueFrom(this.customsClearanceService.processCustomsClearance(clearanceRequest));
      successCount++;
    } catch (error: any) {
      errorCount++;
      errors.push({
        cartonNumber: carton.carrierTrackingNumber,
        error: error.message || 'Unknown error'
      });
      this.batchProgress.errors.push(`${carton.carrierTrackingNumber}: ${error.message || 'Unknown error'}`);
    }

    // Add small delay to prevent overwhelming the server
    await new Promise(resolve => setTimeout(resolve, 100));

    // Process next carton
    this.processBatchClearanceSequentially(cartons, index + 1, successCount, errorCount, errors);
  }

  resetBatchClearance(): void {
    this.batchResult = null;
    this.isMultipleBatchProcessing = false;
    this.batchProgress = {
      processed: 0,
      total: 0,
      errors: []
    };
    this.batchClearanceForm = {
      customsStatus: '',
      clearedAtUtc: '',
      remarks: '',
      destinationRegion: '',
      customsRegion: '',
      customsDirection: '',
      brokerName: '',
      portCode: '',
      clearanceWay: ''
    };
  }

  changeMultipleCartonPage(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.multipleCartonPagination?.totalPages) {
      this.multipleCartonFilters.pageNumber = pageNumber;
      this.loadMultipleCartons();
    }
  }

  getPageNumbers(totalPages: number): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.multipleCartonFilters.pageNumber - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i - 1); // Return 0-based indices for *ngFor
    }
    return pages;
  }

  // Utility methods that were missing
  getCartonStatusLabel(status: string): string {
    switch (status?.toLowerCase()) {
      case 'repacked':
        return 'Repacked';
      case 'intransit':
        return 'In Transit';
      case 'arrivedatdestination':
        return 'Arrived at Destination';
      case 'delivered':
        return 'Delivered';
      case 'customs_pending':
        return 'Customs Pending';
      case 'customs_cleared':
        return 'Customs Cleared';
      default:
        return status || 'Unknown';
    }
  }

  getCartonStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'repacked':
        return 'bg-info';
      case 'intransit':
        return 'bg-primary';
      case 'arrivedatdestination':
        return 'bg-warning';
      case 'delivered':
        return 'bg-success';
      case 'customs_pending':
        return 'bg-warning';
      case 'customs_cleared':
        return 'bg-success';
      default:
        return 'bg-secondary';
    }
  }

  formatDate(dateValue: string | Date | undefined): string {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (error) {
      return 'Invalid Date';
    }
  }

  getCustomsStatusBadgeClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
      case 'pendingdocuments':
        return 'bg-warning';
      case 'underreview':
        return 'bg-info';
      case 'cleared':
        return 'bg-success';
      case 'rejected':
      case 'onhold':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  changeCartonPage(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.totalCartonPages) {
      this.cartonFilters.pageNumber = pageNumber;
      this.loadEligibleCartons();
    }
  }

  changePendingPage(pageNumber: number): void {
    if (pageNumber >= 1 && pageNumber <= this.totalPendingPages) {
      this.pendingFilters.pageNumber = pageNumber;
      this.loadPendingClearances();
    }
  }

  // Validate batch clearance form
  isBatchFormValid(): boolean {
    return !!(
      this.batchClearanceForm.destinationRegion &&
      this.batchClearanceForm.customsRegion &&
      this.batchClearanceForm.customsDirection &&
      this.batchClearanceForm.brokerName &&
      this.batchClearanceForm.portCode &&
      this.batchClearanceForm.clearanceWay
    );
  }

  // Reset batch clearance form
  resetBatchClearanceForm(): void {
    this.batchClearanceForm = {
      customsStatus: '',
      clearedAtUtc: '',
      remarks: '',
      destinationRegion: '',
      customsRegion: '',
      customsDirection: '',
      brokerName: '',
      portCode: '',
      clearanceWay: ''
    };
  }
}



